import { bigQuery, mixpanel } from '@fiverr-private/perseus';
import { getContext } from '@fiverr-private/fiverr_context';
import { merge } from 'lodash';
import {
    MP_CLICKED_COMPARE_PACKAGES,
    MP_CLICKED_CONTACT_ME,
    MP_ClICKED_NAVBAR,
    MP_CLICKED_PACKAGE_SELECT,
    MP_CLICKED_SELLER_NAME_ANCHOR,
    MP_SHOW_PAGE_NAME,
    MP_VIDEO_THUMBNAIL_SIZE,
    MP_CLICKED_GALLERY_FULL_SCREEN,
    MP_CLICKED_BREADCRUMBS_PRO,
    MP_CLICKED_BREADCRUMBS_CATEGORY,
    MP_CLICKED_BREADCRUMBS_SUBCATEGORY,
    MP_CLICKED_BREADCRUMBS_NESTED_SUBCATEGORY,
    MP_CLICKED_TAB,
    MP_CHANGED_RECURRING_OPTION,
    MP_CHANGED_CHECKOUT_PLAN,
    MP_STICKY_EXPERIENCE
} from '../mixPanelEvents';
import {
    createMpComparePackagesParams,
    createMpPackageSelectParams,
    createMpSellerAnchorClickParams,
    createMpShowPageEventParams,
    createMpTopNavClickParams,
    createMpBreadcrumbsClickParams,
    createMpClickedTab,
    createChangedRecurringOption,
    createChangedCheckoutPlan
} from '../mixPanelEventsData';
import {
    BQ_CLICKED_CUSTOM_ORDER,
    BQ_CLICKED_PACKAGE_SELECT,
    BQ_CLICKED_REPORT_GIG,
    BQ_GIG_DESCRIPTION_SEE_MORE,
    BQ_CLICKED_CONTACT_SELLER,
    BQ_CLICKED_PACKAGES_TAB,
    BQ_CLICKED_REVIEWS_SNIPPET_MORE,
    BQ_OPEN_COUPON_ARTICLE,
    BQ_SEEN_BOMBA_COUPON,
    BQ_SELLER_BUYER_SPEAK_SAME_LANGUAGE,
    BQ_PAGE_TRANSLATED_BY_EXTENSION,
    BQ_CLICKED_SELLER_LINK,
    BQ_CLICKED_GALLERY_VIDEO,
    BQ_CLICKED_SELLER_NAME_ANCHOR,
    BQ_CLICKED_REVIEWS_RATINGS,
    BQ_CLICKED_TOPNAV,
    BQ_STICKY_EXPERIENCE,
    BQ_CLICKED_ON_CONTACT_SELLER_OPTIONS,
    BQ_SIGNUP_CLICK
} from '../bigQueryEvents';
import {
    PAGE_NAME,
    createBqCustomOrderClickParams,
    createBqDescriptionSeeMoreParams,
    createBqPackageSelectParams,
    createBqReportGigClickParams,
    createBqContactSellerClickParams,
    createBqClickedPackagesTab,
    createBqReviewsSnippetMoreClickParams,
    createGigGalleryReportParams,
    createOpenCouponArticleParams,
    createSeenCouponParams,
    createBqSellerAndBuyerSpeakSameLanguageParams,
    createBqPageTranslatedByExtensionParams,
    createBqClickedSellerLinkParams,
    createBqClickedReviewsRatingsParams,
    createBqClickedSellerNameAnchor,
    createBqClickedGalleryVideoParams,
    createBqTopNavClickParams,
    createBqStickyExperienceParams,
    createBqContactSellerOptionsClickParams,
    createBqSignupClickParams
} from '../bigQueryEventsData';
import { SINGLE_PACKAGE, TRIPLE_PACKAGE, GIG_GALLERY } from '../events';
import { MIXPANEL_EVENT_TYPES } from '../../types/mixpanelEventTypes';

class BiEvents {

    constructor({ general, packagesCount }) {
        const { userId, pageCtxId, pathParameters } = getContext();
        this.reportingData = {
            ...general,
            userId,
            pageCtxId,
            isPromotedGig: !!pathParameters.ad_key,
            packagesCount
        };
    }

    sendBigQueryEvent({ eventName, params = {} }) {
        if (!this.reportingData) {return;}

        const { packagesCount, pageCtxId } = this.reportingData;

        const generalEventData = {
            gig: { packages_type: packagesCount === 3 ? TRIPLE_PACKAGE : SINGLE_PACKAGE },
            page: { ctx_id: pageCtxId, name: PAGE_NAME }
        };

        const eventParams = { ...this.reportingData, ...params };
        const eventData = this.createBigQueryEventData(eventName, eventParams);
        const event = merge(generalEventData, eventData);

        bigQuery.send(event);
    }

    createBigQueryEventData(eventName, eventParams) {
        switch (eventName) {
            case BQ_CLICKED_PACKAGE_SELECT:
                return createBqPackageSelectParams(eventParams);
            case BQ_CLICKED_CUSTOM_ORDER:
                return createBqCustomOrderClickParams(eventParams);
            case BQ_CLICKED_REPORT_GIG:
                return createBqReportGigClickParams(eventParams);
            case BQ_GIG_DESCRIPTION_SEE_MORE:
                return createBqDescriptionSeeMoreParams(eventParams);
            case BQ_CLICKED_CONTACT_SELLER:
                return createBqContactSellerClickParams(eventParams);
            case BQ_CLICKED_PACKAGES_TAB:
                return createBqClickedPackagesTab(eventParams);
            case BQ_CLICKED_REVIEWS_SNIPPET_MORE:
                return createBqReviewsSnippetMoreClickParams(eventParams);
            case GIG_GALLERY:
                return createGigGalleryReportParams(eventParams);
            case BQ_OPEN_COUPON_ARTICLE:
                return createOpenCouponArticleParams(eventParams);
            case BQ_SEEN_BOMBA_COUPON:
                return createSeenCouponParams(eventParams);
            case BQ_SELLER_BUYER_SPEAK_SAME_LANGUAGE:
                return createBqSellerAndBuyerSpeakSameLanguageParams(eventParams);
            case BQ_PAGE_TRANSLATED_BY_EXTENSION:
                return createBqPageTranslatedByExtensionParams(eventParams);
            case BQ_CLICKED_SELLER_LINK:
                return createBqClickedSellerLinkParams(eventParams);
            case BQ_CLICKED_REVIEWS_RATINGS:
                return createBqClickedReviewsRatingsParams(eventParams);
            case BQ_CLICKED_SELLER_NAME_ANCHOR:
                return createBqClickedSellerNameAnchor(eventParams);
            case BQ_CLICKED_GALLERY_VIDEO:
                return createBqClickedGalleryVideoParams(eventParams);
            case BQ_CLICKED_TOPNAV:
                return createBqTopNavClickParams(eventParams);
            case BQ_STICKY_EXPERIENCE:
                return createBqStickyExperienceParams(eventParams);
            case BQ_CLICKED_ON_CONTACT_SELLER_OPTIONS:
                return createBqContactSellerOptionsClickParams(eventParams);
            case BQ_SIGNUP_CLICK:
                return createBqSignupClickParams(eventParams);
            default:
                return {};
        }
    }

    sendMixPanelEvent({
        mpEventType = MIXPANEL_EVENT_TYPES.STANDARD,
        eventName,
        params = {}
    }) {
        if (!this.reportingData) {return;}

        const eventParams = { ...this.reportingData, ...params };
        const eventData = this.createMixPanelEventData(eventName, eventParams);

        const enrichedEventData = {
            ...this.getEnrichmentData(),
            ...eventData
        };

        if (mpEventType === MIXPANEL_EVENT_TYPES.PAGE_VIEW) {
            mixpanel.trackPageView({
                'Page Name': eventName,
                ...enrichedEventData
            });
        } else {
            mixpanel.track(eventName, enrichedEventData);
        }
    }

    createMixPanelEventData(eventName, eventParams = {}) {
        switch (eventName) {
            case MP_SHOW_PAGE_NAME:
                return createMpShowPageEventParams(eventParams);
            case MP_ClICKED_NAVBAR:
                return createMpTopNavClickParams(eventParams);
            case MP_CLICKED_PACKAGE_SELECT:
                return createMpPackageSelectParams(eventParams);
            case MP_CLICKED_COMPARE_PACKAGES:
                return createMpComparePackagesParams(eventParams);
            case MP_CLICKED_SELLER_NAME_ANCHOR:
                return createMpSellerAnchorClickParams(eventParams);
            case MP_CLICKED_TAB:
                return createMpClickedTab(eventParams);
            case MP_CHANGED_RECURRING_OPTION:
                return createChangedRecurringOption(eventParams);
            case MP_CHANGED_CHECKOUT_PLAN:
                return createChangedCheckoutPlan(eventParams);
            case MP_CLICKED_BREADCRUMBS_PRO:
            case MP_CLICKED_BREADCRUMBS_CATEGORY:
            case MP_CLICKED_BREADCRUMBS_SUBCATEGORY:
            case MP_CLICKED_BREADCRUMBS_NESTED_SUBCATEGORY:
                return createMpBreadcrumbsClickParams(eventParams);
            case MP_CLICKED_CONTACT_ME:
            case MP_VIDEO_THUMBNAIL_SIZE:
            case MP_CLICKED_GALLERY_FULL_SCREEN:
            case MP_STICKY_EXPERIENCE:
            default:
                return eventParams;
        }
    }

    getEnrichmentData() {
        return {
            'Category Id': this.reportingData.categoryId,
            'Sub Category Id': this.reportingData.subCategoryId,
            'Nested Sub Category Id': this.reportingData.nestedSubCategoryId,
            'Is pro': this.reportingData.isPro,
            userId: this.reportingData.userId,
            pageCtxId: this.reportingData.pageCtxId
        };
    }
}

export default BiEvents;
